import React, { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useLocation } from 'react-router-dom';
import { Button } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import useHorizontalResize from 'utils/hooks/useHorizontalResize';

import { RESIZE_LAYOUT } from 'constants/Constants';
import { PATH_IMAGE } from 'constants/Environment';
import { ACTIVITIES, CONVERSATIONS } from 'constants/Entities';
import { ACTIVITY_TYPE_ANNOTATION } from 'models/ActivityModel';
import {
    ActivitiesActions,
    ConfigActions,
    ConversationsActions,
    EntityListActions,
    EntityFiltersActions,
} from 'actions';
import metadata from 'lib/metadata';
import CustomEmptyView from 'containers/components/CustomEmptyView';
import { getLiteral } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';
import { isEmpty } from 'utils/isEmpty';
import Activities from 'components/SvgIcons/emptyScreen/Activities';
import Conversations from 'components/SvgIcons/emptyScreen/Conversations';
import NoData from 'components/SvgIcons/emptyScreen/NoData';

import ActivitiesList from './components/ActivitiesList';
import ActivitiesDetail from './components/ActivitiesDetail';
import ActivitiesTabs from './components/ActivitiesTabs';
import ConversationsList from './components/ConversationsList';
import { getActiveTab } from './utils/conversations';

import './styles.scss';

const EMPTY_OBJECT = {};

const mapStateToProps = (state) => {
    const entityFilters = state?.entityFilters?.[ACTIVITIES.entity] || {};
    const activityFilters = entityFilters.filters || null;
    const activityCrossFilters = entityFilters.crossFilters || EMPTY_OBJECT;
    const activityCrossOperators = entityFilters.crossOperators || EMPTY_OBJECT;
    const activeEntityList = getActiveTab();
    const entityList = state?.entityList?.[activeEntityList] || null;
    const entityDetailIsOpen = state?.entityDetail?.isOpen;
    const tabsCount = Object.entries(state.entityDetail.tabs).length;
    const showActivityDetail =
        tabsCount === 1 || (tabsCount === 0 && !entityDetailIsOpen) || state.entityCrud.isOpen;

    return {
        activityFilters,
        activityCrossFilters,
        activityCrossOperators,
        isLoading: entityList?.loading || false,
        permissions: state.config.permission.crud_permission[ACTIVITIES.permission],
        count: state?.[activeEntityList]?.count || 0,
        isLoadingCount: state?.[activeEntityList]?.isLoadingCount || false,
        entityDetailIsOpen,
        locale: state?.config?.userData?.locale || '',
        enabledUserflow: state?.config?.userData?.userFlow,
        showActivityDetail,
        cachedListWidth: state.config.components?.[RESIZE_LAYOUT.ACTIVITIES.LIST_WIDTH_KEY],
        cachedDetailWidth: state.config.components?.[RESIZE_LAYOUT.ACTIVITIES.DETAIL_WIDTH_KEY],
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        clearFilters: bindActionCreators(EntityFiltersActions, dispatch).clearFilters,
        changeFilter: bindActionCreators(EntityFiltersActions, dispatch).changeFilter,
        changeAdvancedFilter: bindActionCreators(EntityFiltersActions, dispatch)
            .changeAdvancedFilter,
        setIsLoading: bindActionCreators(EntityListActions, dispatch).setIsLoading,
        getActivitiesCount: bindActionCreators(ActivitiesActions, dispatch).getActivitiesCount,
        getConversationsCount: bindActionCreators(ConversationsActions, dispatch)
            .getConversationsCount,
        setConfigWeb: bindActionCreators(ConfigActions, dispatch).setConfigWeb,
    };
};

const Content = memo(
    ({
        activityFilters,
        activityCrossFilters,
        activityCrossOperators,
        isLoading,
        clearFilters,
        changeFilter,
        changeAdvancedFilter,
        permissions,
        setIsLoading,
        getActivitiesCount,
        getConversationsCount,
        count,
        isLoadingCount,
        entityDetailIsOpen,
        locale,
        enabledUserflow,
        showActivityDetail,
        cachedListWidth,
        cachedDetailWidth,
        setConfigWeb,
    }) => {
        const [activeTab, setActiveTab] = useState(() => getActiveTab());
        const [unreadMessages, setUnreadMessages] = useState({});
        const isFirstLoad = useRef(true);
        const [detailWidth, setDetailWidth] = useState(
            cachedDetailWidth || localStorage.getItem(RESIZE_LAYOUT.ACTIVITIES.DETAIL_WIDTH_KEY),
        );
        const activeTabRef = useRef(activeTab);
        const hasCalledCount = useRef(false);
        const prevFiltersRef = useRef(null);
        const currentFiltersRef = useRef(null);
        const wrapRef = useRef(null);
        const containerRef = useRef(null);

        const location = useLocation();

        const finalWidth =
            cachedListWidth || localStorage.getItem(RESIZE_LAYOUT.ACTIVITIES.LIST_WIDTH_KEY);

        const hasActiveFilters = useMemo(() => {
            return (
                (activityFilters && Object.keys(activityFilters).length > 0) ||
                (!isEmpty(activityCrossFilters) && !isEmpty(activityCrossFilters, true)) ||
                (!isEmpty(activityCrossOperators) && !isEmpty(activityCrossOperators, true))
            );
        }, [activityCrossFilters, activityCrossOperators, activityFilters]);

        useEffect(() => {
            const newActiveTab = getActiveTab(location);
            if (newActiveTab !== activeTab) {
                setActiveTab(newActiveTab);
            }
        }, [activeTab, location]);

        const handleResizeStart = useCallback(() => {
            logEvent({
                event: ACTIVITIES.trueName,
                functionality: 'feedResize',
            });
        }, []);

        const handleResize = useCallback(
            ({ percentageList, percentageDetail }) => {
                const listWidth = `${percentageList}%`;
                const detailWidthValue = `${percentageDetail}%`;
                localStorage.setItem(RESIZE_LAYOUT.ACTIVITIES.DETAIL_WIDTH_KEY, detailWidthValue);
                localStorage.setItem(RESIZE_LAYOUT.ACTIVITIES.LIST_WIDTH_KEY, listWidth);
                setConfigWeb(RESIZE_LAYOUT.ACTIVITIES.LIST_WIDTH_KEY, listWidth);
                setConfigWeb(RESIZE_LAYOUT.ACTIVITIES.DETAIL_WIDTH_KEY, detailWidthValue);
                setDetailWidth(detailWidthValue);
            },
            [setConfigWeb],
        );

        useHorizontalResize({
            targetRef: wrapRef,
            containerRef,
            minWidth: RESIZE_LAYOUT.ACTIVITIES.MIN_WIDTH,
            minPercentage: RESIZE_LAYOUT.ACTIVITIES.MIN_PERCENTAGE,
            maxPercentage: RESIZE_LAYOUT.ACTIVITIES.MAX_PERCENTAGE,
            onResizeStart: handleResizeStart,
            onResize: handleResize,
        });

        useEffect(() => {
            // Keep current filters reference updated for the finally callback
            currentFiltersRef.current = JSON.stringify({
                filters: activityFilters,
                crossFilters: activityCrossFilters,
                crossOperators: activityCrossOperators,
            });

            const filtersChanged = prevFiltersRef.current !== currentFiltersRef.current;
            const tabChanged = activeTabRef.current !== activeTab;

            // Skip if already loading or if neither tab nor filters changed
            if (hasCalledCount.current) return;
            if (!tabChanged && !filtersChanged && prevFiltersRef.current !== null) return;

            hasCalledCount.current = true;

            let getCount, relatedEntity;

            if (activeTab === CONVERSATIONS.trueName) {
                getCount = getConversationsCount;
                relatedEntity = ACTIVITIES;
            } else {
                getCount = getActivitiesCount;
            }

            getCount(relatedEntity).finally(() => {
                // Update filter signature AFTER the count finishes to capture any
                // preloaded filters that were added during the request
                prevFiltersRef.current = currentFiltersRef.current;
                hasCalledCount.current = false;
            });
        }, [
            activeTab,
            getActivitiesCount,
            getConversationsCount,
            activityFilters,
            activityCrossFilters,
            activityCrossOperators,
        ]);

        useEffect(() => {
            let entity, relatedEntity;

            if (activeTab !== activeTabRef.current || isFirstLoad.current) {
                if (activeTab === ACTIVITIES.trueName) {
                    entity = ACTIVITIES;
                    relatedEntity = null;
                } else {
                    entity = CONVERSATIONS;
                    relatedEntity = CONVERSATIONS;
                }

                setIsLoading(entity, true);

                // Clear search when switching between ACTIVITIES/CONVERSATIONS tabs
                changeFilter({
                    entity: ACTIVITIES,
                    filter: {
                        id: 'matchingName',
                        dataType: 'search',
                        hideForCount: true,
                    },
                    value: null,
                    refresh: false,
                    completeValues: null,
                    isEntityList: true,
                    info: null,
                    isPreload: false,
                    relatedEntity,
                });

                isFirstLoad.current = false;
                activeTabRef.current = activeTab;
            }
        }, [activeTab, location, setIsLoading, changeFilter]);

        const shouldRenderEmptyScreen = useMemo(() => {
            if (!hasActiveFilters && count === 0 && !isLoadingCount && !isLoading) {
                return true;
            }

            return false;
        }, [hasActiveFilters, count, isLoadingCount, isLoading]);

        const shouldRenderNoResults = useMemo(() => {
            if (hasActiveFilters && count === 0 && !isLoadingCount && !isLoading) {
                return true;
            }

            return false;
        }, [hasActiveFilters, count, isLoadingCount, isLoading]);

        const emptyIcon = useMemo(() => {
            if (locale.includes('es-'))
                return <img src={`${PATH_IMAGE}illustration_video_activities_ES.png`} />;
            return <img src={`${PATH_IMAGE}illustration_video_activities_EN.png`} />;
        }, [locale]);

        const emptyViewProps = useCallback(() => {
            if (activeTab === CONVERSATIONS.trueName) {
                return {
                    title: getLiteral('label_empty_screen_my_conversation'),
                    subtitle: getLiteral('label_empty_screen_my_conversations_desc'),
                    icon: <Conversations />,
                    button: (
                        <Button
                            className="fm-activities__detail--empty-button"
                            type="secondary"
                            onClick={() => ensureRoute(ACTIVITIES.route)}
                        >
                            {getLiteral('action_go_to_activities')}
                        </Button>
                    ),
                    others: {
                        callsToAction: permissions?.create
                            ? [
                                  <Button
                                      key="add-activity"
                                      className="fm-activities__detail--empty-button"
                                      type="secondary"
                                      onClick={() =>
                                          ensureRoute(
                                              `${CONVERSATIONS.route}${ACTIVITIES.route}/new/${ACTIVITY_TYPE_ANNOTATION}`,
                                          )
                                      }
                                  >
                                      {getLiteral('action_add_activity')}
                                  </Button>,
                              ]
                            : null,
                    },
                };
            } else {
                if (!metadata.showEmptyScreenVideo || enabledUserflow === 'False')
                    return {
                        title: getLiteral('label_empty_screen_activities'),
                        subtitle: getLiteral('label_empty_screen_activities_desc'),
                        icon: <Activities />,
                        iconType: 'icon',
                        button: permissions?.create ? (
                            <Button
                                className="fm-activities__detail--empty-button"
                                type="secondary"
                                onClick={() =>
                                    ensureRoute(
                                        `${ACTIVITIES.route}/new/${ACTIVITY_TYPE_ANNOTATION}`,
                                    )
                                }
                            >
                                {getLiteral('action_add_activity')}
                            </Button>
                        ) : null,
                        others: {
                            callsToAction: [
                                <Button
                                    key="sync-email"
                                    type="secondary"
                                    onClick={() => ensureRoute(`/settings/nylas`)}
                                >
                                    {getLiteral('action_sync_email')}
                                </Button>,
                            ],
                        },
                    };
                return {
                    className: 'fm-empty-screen__container-activities',
                    title: getLiteral('label_empty_screen_activities'),
                    subtitle: getLiteral('label_empty_screen_activities_desc'),
                    icon: emptyIcon,
                    iconType: 'video',
                    onTracking: () => {
                        logEvent({
                            event: ACTIVITIES.trueName,
                            submodule: 'emptyScreen',
                            functionality: 'video',
                        });
                    },
                    button: permissions?.create ? (
                        <Button
                            key="add-activity"
                            className="fm-activities__detail--empty-button"
                            type="secondary"
                            onClick={() =>
                                ensureRoute(`${ACTIVITIES.route}/new/${ACTIVITY_TYPE_ANNOTATION}`)
                            }
                        >
                            {getLiteral('action_add_activity')}
                        </Button>
                    ) : null,
                    others: {
                        callsToAction: [
                            <Button
                                key="sync-email"
                                type="secondary"
                                onClick={() => ensureRoute(`/settings/nylas`)}
                            >
                                {getLiteral('action_sync_email')}
                            </Button>,
                        ],
                    },
                };
            }
        }, [activeTab, emptyIcon, permissions?.create, enabledUserflow]);

        const onClearFilters = useCallback(() => {
            logEvent({
                event: ACTIVITIES.trueName,
                submodule: 'emptyScreen',
                functionality: 'clearFilter',
            });

            clearFilters({ entity: ACTIVITIES, isAPurge: false });
            changeAdvancedFilter({ entity: ACTIVITIES, operators: {} });
        }, [clearFilters, changeAdvancedFilter]);

        const noResultsViewProps = useCallback(
            () => ({
                title: getLiteral('error_notresultfound'),
                subtitle: getLiteral('helptext_search_filter_text'),
                icon: <NoData />,
                button: (
                    <Button
                        className={`${ACTIVITIES.entity}-content__empty-view-actions__text`}
                        type="secondary"
                        onClick={onClearFilters}
                    >
                        {getLiteral('action_clear_filters')}
                    </Button>
                ),
            }),
            [onClearFilters],
        );

        const listComponent = useMemo(() => {
            switch (activeTab) {
                case CONVERSATIONS.trueName:
                    return (
                        <ConversationsList setUnreadMessages={setUnreadMessages} count={count} />
                    );
                case ACTIVITIES.trueName:
                default:
                    return <ActivitiesList setUnreadMessages={setUnreadMessages} count={count} />;
            }
        }, [activeTab, count]);

        const isEmptyScreen = useMemo(
            () => shouldRenderEmptyScreen || shouldRenderNoResults,
            [shouldRenderEmptyScreen, shouldRenderNoResults],
        );
        const wrapProps = useMemo(
            () => ({
                className: classnames('fm-activities-wrap', {
                    'fm-activities-wrap--empty': isEmptyScreen && !entityDetailIsOpen,
                }),
                style: !isEmptyScreen || entityDetailIsOpen ? { width: finalWidth } : null,
            }),
            [finalWidth, isEmptyScreen, entityDetailIsOpen],
        );

        return (
            <div ref={containerRef} className="activities-content">
                <div ref={wrapRef} {...wrapProps}>
                    <ActivitiesTabs
                        activeTab={activeTab}
                        className="fm-activities-tabs"
                        onChange={(tab) => setActiveTab(tab)}
                    />
                    {shouldRenderEmptyScreen && <CustomEmptyView emptyViewProps={emptyViewProps} />}
                    {shouldRenderNoResults && (
                        <CustomEmptyView emptyViewProps={noResultsViewProps} />
                    )}
                    {!isEmptyScreen && listComponent}
                </div>
                {(!isEmptyScreen || entityDetailIsOpen) && showActivityDetail && (
                    <ActivitiesDetail unreadMessages={unreadMessages} detailWidth={detailWidth} />
                )}
            </div>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(Content);
